import React from 'react';
import { Button, TextInput } from '@gafe/common-ui';
import merge from 'classnames';
import { Formik } from 'formik';
import omit from 'lodash/omit';

import { InputGroup } from '@/components/generic/InputGroup';
import { RadioButton } from '@/components/generic/RadioButton';
import { AddressInputs } from '@/components/shared/AddressInputs';
import { formatPhoneNumber } from '@/utils/format-phone-number';

import { getInitialValues, validationSchema } from './constants';
import { FormValuesKeys, FProps, InputChangeEvent, Props } from './types';

import styles from './AddressForm.module.scss';

export const AddressForm = (props: Props) => {
  const { addressToEdit, isShippingAddressListEmpty, submit, cancel } = props;

  const onInputChange = (fProps: FProps) => (event: InputChangeEvent) => {
    const name = event.target.name as FormValuesKeys;

    if (fProps.errors[name]) {
      fProps.setErrors(omit(fProps.errors, [name]));
    }

    fProps.handleChange(event);
  };

  const onFieldError =
    (fProps: FProps) =>
    (fieldName: FormValuesKeys): string | undefined => {
      return fProps.touched[fieldName] && fProps.errors[fieldName] ? fProps.errors[fieldName] : '';
    };

  return (
    <div>
      <Formik
        initialValues={getInitialValues(addressToEdit, isShippingAddressListEmpty)}
        onSubmit={() => void 0}
        validationSchema={validationSchema}
      >
        {formikProps => {
          const { dirty, errors, values, setFieldValue, resetForm, handleBlur } = formikProps;
          const handleFieldError = onFieldError(formikProps);
          const handleFieldChange = onInputChange(formikProps);

          const isEditForm = addressToEdit !== null;
          const isErrors = Object.keys(errors).length !== 0;
          const isNewFormDisabled = !dirty || isErrors;
          const isDisabled = isEditForm ? isErrors : isNewFormDisabled;

          return (
            <>
              <div className={styles.row}>
                <div className={styles.title}>Add New Saved Address</div>
              </div>
              <InputGroup>
                <TextInput
                  errorMessage={handleFieldError('firstName')}
                  hasError={Boolean(handleFieldError('firstName'))}
                  labelText="First Name"
                  maxLength={255}
                  name="firstName"
                  onBlur={handleBlur}
                  onChange={handleFieldChange}
                  role="textbox"
                  showRequired
                  value={values.firstName}
                />
                <TextInput
                  errorMessage={handleFieldError('lastName')}
                  hasError={Boolean(handleFieldError('lastName'))}
                  labelText="Last Name"
                  maxLength={255}
                  name="lastName"
                  onBlur={handleBlur}
                  onChange={handleFieldChange}
                  showRequired
                  value={values.lastName}
                />
              </InputGroup>
              <InputGroup>
                <TextInput
                  errorMessage={handleFieldError('phoneNumber')}
                  hasError={Boolean(handleFieldError('phoneNumber'))}
                  labelText="Phone"
                  maxLength={12}
                  name="phoneNumber"
                  onBlur={handleBlur}
                  onChange={handleFieldChange}
                  showRequired
                  value={formatPhoneNumber(values.phoneNumber)}
                />
                <TextInput
                  errorMessage={handleFieldError('email')}
                  hasError={Boolean(handleFieldError('email'))}
                  labelText="Email"
                  maxLength={255}
                  name="email"
                  onBlur={handleBlur}
                  onChange={handleFieldChange}
                  showRequired
                  value={values.email}
                />
              </InputGroup>
              <AddressInputs
                errorType="inline"
                labels="simple"
                placeholder="Street address, company name, etc"
              />
              <InputGroup>
                <div
                  className={styles.default_address}
                  onClick={() => {
                    if (!isShippingAddressListEmpty) {
                      setFieldValue('isDefault', !values.isDefault);
                    }
                  }}
                >
                  <RadioButton
                    className={merge({ [styles.radio_button_selected]: values.isDefault })}
                    selected={values.isDefault}
                  />
                  <span style={{ marginLeft: 10 }}>Set as default address</span>
                </div>
              </InputGroup>
              {props.submitError.length !== 0 && (
                <InputGroup>
                  <div className={styles.submit_error}>{props.submitError}</div>
                </InputGroup>
              )}
              <div className={styles.stepper_buttons}>
                <Button
                  colorSchema="neutral"
                  onClick={() => {
                    resetForm();
                    cancel();
                  }}
                  size="large"
                  variant="text"
                >
                  Cancel
                </Button>
                <Button
                  colorSchema="primary"
                  disabled={isDisabled}
                  onClick={() => submit(values)}
                  size="large"
                  testId="save-address-button"
                  variant="contained"
                >
                  Save Address
                </Button>
              </div>
            </>
          );
        }}
      </Formik>
    </div>
  );
};
